import React, { createContext, useRef, useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import { dateToPFormat, pFormatToDate, useValidation } from './helpers'
import Popper from '../Popper'
import { addMonths, format, isValid } from 'date-fns'
import { alpha, useTheme } from '@mui/material'

export const DatePickerContext = createContext()

export const DatePickerProvider = ({
  children,
  field,
  locale,
  maxDate,
  minDate,
  defaultStartDate = new Date(),
  defaultEndDate,
  defaultPreset,
  blockFutureDates = false,
  blockPastDates = false,
  showPresetLabel = false,
  placement = 'bottom-start',
  singleDateMode = false,
  editMode = false,
  onChange = () => {},
  onSubmit = () => {},
}) => {
  const formMethods = useFormContext()
  const theme = useTheme()

  if (!formMethods) {
    throw new Error('The DatePicker component must be wrapped in a react-hook-form context')
  }

  const [dateNameSelected, setDateNameSelected] = useState(editMode ? 'endDate' : 'startDate')
  const [referenceDay, setReferenceDay] = useState(defaultStartDate || new Date())
  const { getValues, setValue, setError, clearErrors } = useFormContext()

  const displayedMonth = useMemo(() => {
    return referenceDay && isValid(referenceDay) ? referenceDay.getMonth() : new Date().getMonth()
  }, [referenceDay])
  
  const popperEl = useRef()
  const validation = useValidation({ locale })

  useEffect(() => {
    if (defaultPreset) {
      setValue('presetSelection', defaultPreset)
    }
  }, [defaultPreset, setValue])

  const handleReset = () => {
    if (!editMode) {
      setValue('startDate', '')
      setValue('endDate', '')
      setValue('presetSelection', null)
      clearErrors()
      setDateNameSelected('startDate')
      setReferenceDay(defaultStartDate || new Date())
      onChange(singleDateMode ? null : null, null)
    } else {
      setValue('endDate', '')
      clearErrors()
      setDateNameSelected('endDate')
      onChange(defaultStartDate, null)
    }
  }

  useEffect(() => {
    const currentStartDate = getValues('startDate')
    const currentEndDate = getValues('endDate')
    
    if (!currentStartDate && defaultStartDate) {
      setValue('startDate', dateToPFormat(defaultStartDate, locale))
    }
    
    if (!currentEndDate && defaultEndDate && !singleDateMode) {
      setValue('endDate', dateToPFormat(defaultEndDate, locale))
    }
  }, [locale, defaultEndDate, defaultStartDate, setValue, getValues, singleDateMode])

  const popperModifiers = useMemo(() => {
    return [
      {
        name: 'offset',
        options: {
          offset: [0, 8],
        },
      },
    ]
  }, [])

  return (
    <DatePickerContext.Provider
      value={{
        onChange: (startDate, endDate) => {
          if (singleDateMode && startDate) {
            setReferenceDay(startDate)
          }
          if (!editMode) {
            onChange(startDate, endDate)
          } else {
            onChange(defaultStartDate, endDate)
          }
        },
        displayedMonth,
        referenceDay: referenceDay || new Date(),
        locale,
        minDate: editMode ? defaultStartDate : (blockPastDates ? new Date() : minDate),
        maxDate: blockFutureDates ? new Date() : maxDate,
        blockFutureDates,
        blockPastDates,
        showPresetLabel,
        singleDateMode,
        editMode,
        onChangeMonth: (operation) => {
          const currentRef = referenceDay || new Date()
          const newDate = addMonths(currentRef, operation)
          const today = new Date()

          if (blockFutureDates) {
            const isAfterCurrentMonth =
              newDate.getFullYear() > today.getFullYear() ||
              (newDate.getFullYear() === today.getFullYear() &&
                newDate.getMonth() > today.getMonth())
            if (isAfterCurrentMonth) {
              return
            }
          }

          if (blockPastDates && !editMode) {
            const isBeforeCurrentMonth =
              newDate.getFullYear() < today.getFullYear() ||
              (newDate.getFullYear() === today.getFullYear() &&
                newDate.getMonth() < today.getMonth())
            if (isBeforeCurrentMonth) {
              return
            }
          }

          if (editMode && defaultStartDate) {
            const startMonth = new Date(defaultStartDate)
            const isBeforeStartMonth =
              newDate.getFullYear() < startMonth.getFullYear() ||
              (newDate.getFullYear() === startMonth.getFullYear() &&
                newDate.getMonth() < startMonth.getMonth())
            if (isBeforeStartMonth) {
              return
            }
          }

          setReferenceDay(newDate)
        },
        onCancel: () => {
          popperEl.current.close()
        },
        onReset: handleReset,
        onApply: () => {
          clearErrors()
          
          const startDateValue = getValues('startDate')
          const endDateValue = getValues('endDate')
          
          const startDateValidation = editMode ? { isValid: true, error: null } : validation.validateStartDate(startDateValue)
          const endDateValidation = singleDateMode ? { isValid: true, error: null } : validation.validateEndDate(endDateValue)

          if (startDateValidation.isValid && endDateValidation.isValid) {
            const parsedStartDate = startDateValue ? pFormatToDate(startDateValue, locale) : null
            const parsedEndDate = singleDateMode ? null : (endDateValue ? pFormatToDate(endDateValue, locale) : null)
            
            onSubmit(parsedStartDate, parsedEndDate)
            popperEl.current.close()
          } else {
            if (!startDateValidation.isValid && !editMode) {
              setError('startDate', {
                type: 'manual',
                message: startDateValidation.error,
              })
            }
            if (!endDateValidation.isValid && !singleDateMode) {
              setError('endDate', {
                type: 'manual',
                message: endDateValidation.error,
              })
            }
          }
        },
        startDate: getValues('startDate'),
        endDate: getValues('endDate'),
        defaultStartDate: defaultStartDate ? format(defaultStartDate, 'P', { locale }) : '',
        defaultEndDate: defaultEndDate ? format(defaultEndDate, 'P', { locale }) : '',
        dateNameSelected,
        setDateNameSelected,
      }}
    >
      <Popper
        anchorElt={field}
        placement={placement}
        ref={popperEl}
        onClickOutside={() => {
          handleReset()
          popperEl.current.close()
        }}
        style={{
          zIndex: 9999,
        }}
        paperstyle={{
          borderRadius: 16,
          boxShadow: `0 24px 32px -12px ${alpha(theme.palette.common.black, 0.16)}`,
        }}
        modifiers={popperModifiers}
      >
        {children}
      </Popper>
    </DatePickerContext.Provider>
  )
}
